import { PureComponent } from 'react'
import { compose } from 'recompose'
import _ from 'lodash'
import cx from 'classnames'
import { createI13nNode } from '@vzmi/ec-react-i13n'
import { Helmet } from 'react-helmet'
import HOC from '../../hoc/loading'
import styles from './UnsalableItems.css'
import { pullLeft } from '../../styles/shared.css'

import { withI13nWrapper } from '../Common/I13nWrapper'
import ExportButton from '../Common/ExportButton'
import YSelect from '../Common/YSelect'
import YTable from '../Common/YTable'
import Information from '../Common/Information'
import { YCard } from '../Common/YCard'

import { sendI13nClickEvent } from '../../../lib/i13nUtil'
import { convertDateByMoment, getDefaultTimezoneMoment } from '../../../lib/renderUtil'
import { DEFAULT_PAGINATION, DEFAULT_UNSALABLE_THRESHOLD, EXPORT_TEMPLATE_SETTING } from '../../constants/globalTypes'
import { ExcelExport, ExcelExportHelper, FetchDataUtil } from '@vzmi/component_ec_nodejs_util'
import withConnectedExport from '../../containers/Common/WithConnectedExport'
import {
  GA_CLICK_ACTION,
  getI13nModel
} from './UnsalableItems.ga'
import { YESTERDAY } from '../../constants/date'

const ExportHelper = new ExcelExportHelper(EXPORT_TEMPLATE_SETTING.unsalableItems)

/** page title */
const HelmetSetting = () => (
  <Helmet>
    <title>滯銷品項清單</title>
  </Helmet>
)

const columns = [
  {
    id: 'index',
    Header: '項次',
    Cell: row => row.page * row.pageSize + row.index + 1,
    width: 60,
    maxWidth: 60
  },
  {
    Header: '商品序號',
    accessor: 'productId',
    width: 90,
    maxWidth: 90
  },
  {
    Header: '商品名稱',
    accessor: 'name',
    className: cx(pullLeft, styles.productName),
    width: 480,
    maxWidth: 480
  },
  {
    Header: '滯銷天數',
    accessor: 'unsalableDays'
  },
  {
    Header: '庫存',
    accessor: 'volume'
  }
]

const UNSALABLE_THRESHOLD_OPTIONS = [10, 20, 30, 60, 90, 180].map((days) => {
  return { label: `${days}天`, value: days }
})

const UnsalableThresholdSelect = ({ value, onChange, ...props }) => {
  return (
    <div className={styles.UnsalableThresholdSelect}>
      <span key='days'>滯銷天數：</span>
      <YSelect
        key='select'
        data-role='unsalable-threshold--select'
        value={value}
        options={UNSALABLE_THRESHOLD_OPTIONS}
        onChange={onChange}
        simpleValue
        size='small'
        {...props}
      />
    </div>
  )
}

export class UnsalableItems extends PureComponent {
  constructor (props) {
    super(props)
    // 資料統計至前一天
    const periodText = convertDateByMoment(YESTERDAY, 'YYYY/MM/DD')
    this.state = {
      unsalableThreshold: DEFAULT_UNSALABLE_THRESHOLD,
      data: [],
      page: 0,
      pages: -1,
      pageSize: DEFAULT_PAGINATION.perPage,
      resultsTotal: 0,
      periodText
    }
  }

  onPageSizeChange = (pageSize, page) => {
    this.setState({
      page,
      pageSize
    })
    this.sendPageview({
      type: GA_CLICK_ACTION.SHOW_PAGES
    })
  }

  onUnsalableThresholdChange = (unsalableThreshold) => {
    const { page: currentPage, unsalableThreshold: currentThreshold } = this.state
    if (currentThreshold !== unsalableThreshold) {
      this.setState(
        () => ({
          unsalableThreshold
        }),
        () => {
          /** trigger pageChange event of react-table if current page on page 1
           * By doing this to prevent page from ignoring by react-table that stopped fetching */
          if (currentPage !== 0) {
            this.table.getWrappedInstance().onPageChange(0)
          } else {
            this.table.getWrappedInstance().fireFetchData()
          }
        })
      this.sendPageview({
        type: GA_CLICK_ACTION.UNSALE_DROPDOWN
      })
    }
  }

  fetchTableData = ({ unsalableThreshold, pageSize, page, offset }) => {
    const payload = {
      unsalableThreshold,
      sort: '+unsalableDays',
      limit: pageSize,
      offset
    }
    const fetchPromise = this.props.actions.fetchUnsalableItems(payload)
      .then((res) => {
        if (!res.error) {
          const data = _.get(res, 'payload.unsalableItems', [])
          const pagination = _.get(res, 'payload.pagination', { resultsTotal: 0 })
          const { resultsTotal } = pagination
          const postRes = {
            rows: data,
            pages: Math.ceil(resultsTotal / pageSize),
            resultsTotal: pagination.resultsTotal
          }
          return postRes
        }
        return {}
      })

    /** set up loading state */
    this.props.loading((done) => {
      fetchPromise
        .then(done).catch(done)
    })
    return fetchPromise
  }

  fetchData = (state, instance) => {
    this.setState({ allowExport: false, loading: true })
    // 資料參數為滯銷天數 預設30天
    const { unsalableThreshold } = this.state
    const { page, pageSize } = state
    const offset = (page === 0) ? 0 : page * pageSize
    const payload = { unsalableThreshold, pageSize, page, offset }
    this.fetchTableData(payload)
      .then(res => {
        this.setState({
          page,
          data: res.rows || [],
          pages: res.pages || 1,
          resultsTotal: res.resultsTotal || 0,
          allowExport: !!res.resultsTotal
        })
      })
  }

  exportExcel = () => {
    const {
      unsalableThreshold: threshold,
      pageSize,
      allowExport
    } = this.state
    const { actions, openExportModal, closeExportModal } = this.props

    if (!allowExport) {
      return
    }

    this.sendPageview({
      type: GA_CLICK_ACTION.EXPORT_EXCEL
    })
    openExportModal()

    const payload = {
      unsalableThreshold: threshold,
      sort: '+unsalableDays',
      limit: pageSize
    }
    FetchDataUtil.fetchAllPages(actions.fetchUnsalableItems, { payload, selector: res => _.get(res, 'unsalableItems', []) })
      .then((res) => {
        const {
          data
        } = res
        const option = {
          fileName: ExportHelper.getFileName({ threshold, nowDate: getDefaultTimezoneMoment() }),
          sheets: [
            {
              name: ExportHelper.getSheetName({ threshold }),
              headers: ExportHelper.getSheetHeaders(),
              data: ExportHelper.convertDataByHeaders(data)
            }
          ]
        }
        const ExcelExporter = new ExcelExport(option)
        ExcelExporter.download({ bookType: ExportHelper.getBookType() })

        closeExportModal()
      })
  }

  computePagination = () => {
    const {
      page,
      pageSize,
      resultsTotal
    } = this.state

    const totalCount = resultsTotal || 0
    let pageCount = totalCount === 0 ? totalCount : (page + 1) * +pageSize
    pageCount = totalCount < pageCount ? totalCount : pageCount
    return {
      pageCount,
      totalCount
    }
  }

  sendPageview = (action) => {
    const { i13n } = this.props
    const i13nModel = getI13nModel(action)
    sendI13nClickEvent({ i13n, i13nModel })
  }

  onPageChange = () => {
    this.sendPageview({
      type: GA_CLICK_ACTION.PAGINATION
    })
  }

  render () {
    const { isLoading } = this.props
    const { unsalableThreshold, periodText, data, pages, allowExport } = this.state
    const {
      pageCount,
      totalCount
    } = this.computePagination()
    const clz = cx(styles.UnsalableItems)
    const pageIndicator = pageCount === 0 ? '--' : `${pageCount} / ${totalCount}`

    return (
      <div
        className={clz}
        data-role='unsalable-items'
      >
        <HelmetSetting />
        <div className={styles.pageCount}>
          {`共 ${pageIndicator} 筆`}
          <UnsalableThresholdSelect
            value={unsalableThreshold}
            onChange={this.onUnsalableThresholdChange}
          />
          <ExportButton
            data-role='unsalable-items-export-button'
            className={styles.exportButton}
            disabled={!allowExport}
            onClick={() => this.exportExcel()}
          />
          <Information
            className={styles.info}
            description={`資料統計至：${periodText}`}
          />
        </div>
        <YCard className={styles.productList}>
          <YTable
            ref={r => (this.table = r)}
            manual // informs React Table that you'll be handling sorting and pagination server-side
            data={data}
            pages={pages} // Display the total number of pages
            loading={isLoading} // Display the loading overlay when we need it
            columns={columns}
            onFetchData={this.fetchData} // Request new data when things change
            onPageSizeChange={this.onPageSizeChange}
            onPageChange={this.onPageChange}
          />
        </YCard>
      </div>
    )
  }
}

const hoc = compose(
  withConnectedExport,
  createI13nNode,
  HOC,
  withI13nWrapper
)

export default hoc(UnsalableItems)
